import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  DateField,
} from '@structural-climbing/ui'
import type { TrainerAssignment } from './useTrainerAssignments'

interface Props {
  assignment: TrainerAssignment
  onClose: () => void
  onSaved: () => void
}

/** Adds `days` to a YYYY-MM-DD string without dragging a timezone into it. */
function addDays(day: string, days: number): string {
  const d = new Date(`${day}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

/**
 * Pushes an assignment's end date out — the trainer took a fortnight off, so the
 * member's block runs a fortnight longer.
 *
 * The quick buttons exist because that is how the request actually arrives
 * ("he's away next week, give her the time back"), and counting days on a date
 * picker is where mistakes happen.
 */
export function ExtendAssignmentModal({ assignment, onClose, onSaved }: Props) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [endsAt, setEndsAt] = useState(assignment.ends_at ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const base = assignment.ends_at ?? new Date().toISOString().slice(0, 10)

  async function handleSubmit() {
    setSaving(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const resp = await fetch(`${apiUrl}/trainer-assignments/${assignment.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ends_at: endsAt || null }),
      })
      if (!resp.ok) {
        const { error: msg } = (await resp.json()) as { error?: string }
        throw new Error(msg ?? t('trainers.errorExtend'))
      }
      onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('trainers.errorExtend'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal scrollBehavior="inside" isOpen onOpenChange={(open) => !open && onClose()}>
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex-col items-start gap-1">
              <span>{t('trainers.extendTitle')}</span>
              <span className="text-sm font-normal text-foreground/60">
                {assignment.child_name
                  ? `${assignment.trainer_name} · ${assignment.child_name}`
                  : assignment.trainer_name}
              </span>
            </ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap gap-2">
                  {[7, 14, 30].map((days) => (
                    <Button
                      key={days}
                      size="sm"
                      variant="flat"
                      onClick={() => setEndsAt(addDays(base, days))}
                    >
                      {t('trainers.plusDays', { count: days })}
                    </Button>
                  ))}
                </div>
                <DateField
                  label={t('trainers.endsAtLabel')}
                  value={endsAt}
                  onValueChange={setEndsAt}
                  description={t('trainers.endsAtHint')}
                />
                {error && <p className="text-sm text-danger">{error}</p>}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="flat" onClick={onClose} isDisabled={saving}>
                {t('common.cancel')}
              </Button>
              <Button color="primary" onClick={handleSubmit} isLoading={saving}>
                {t('common.save')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
